/**
 * Video constants + plan gating — the single source of truth for what counts as
 * an uploadable video and how much video a given plan may host.
 *
 * ISOMORPHIC. This module is deliberately dependency-free and carries NO
 * `server-only` marker, so it is safe to import from both client components (the
 * composer / upload widget, for early UX errors) and server routes (signed-URL
 * creation, the confirm endpoint). Keeping one definition here means the client
 * and server can never drift on the accepted MIME set or the per-plan caps.
 *
 * ENFORCEMENT MODEL. `VIDEO_MAX_UPLOAD_BYTES_HARD` is the absolute per-video
 * ceiling the CLIENT uses to reject a too-large file THE MOMENT IT IS PICKED —
 * before it ever touches the network. Every plan's `maxVideoSizeBytes` equals
 * this ceiling today, so the pick-time check and the server checks agree and an
 * oversize file can never get past the composer only to fail later mid-send.
 * The SERVER still enforces `maxVideoSizeBytes` at signed-URL creation and
 * AGAIN at confirm time, because a client cannot be trusted to police its own
 * quota. `videoLimitsForPlan` is the one function both server checks read from,
 * so the enforcement points can never disagree.
 */

// Supabase Storage bucket that holds uploaded project videos. The confirm
// endpoint writes here; public playback URLs are derived from it.
export const VIDEO_BUCKET = "project-videos";

// The MIME types a browser upload may declare. Kept narrow on purpose: these are
// the container formats Panda can reliably store and play back (or hand to the
// transcoder). Anything outside this set is rejected client-side and again on
// the server. Note `video/quicktime` covers `.mov` and `video/x-m4v` covers the
// iOS `.m4v` variant Safari sometimes reports.
export const VIDEO_ACCEPTED_TYPES: readonly string[] = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/x-m4v",
];

// Byte helpers — kept as explicit products so the numbers are auditable.
const MB = 1024 * 1024;
const GB = 1024 * MB;

// Absolute per-video ceiling (1 GB). A file larger than this is rejected in
// the browser at file-pick time, without a network round-trip, regardless of
// plan — and the server enforces the same number, so the rejection always
// happens up front rather than after a send attempt.
export const VIDEO_MAX_UPLOAD_BYTES_HARD = 1 * GB;

/**
 * Map an upload MIME type to the file extension we persist it under. Only the
 * accepted container types have a dedicated extension; anything else (including
 * null/undefined/unknown) falls back to "mp4", the safest broadly-playable
 * default, so a storage key is always well-formed.
 */
export function videoExtForMime(mime: string | null | undefined): string {
  switch ((mime ?? "").toLowerCase().trim()) {
    case "video/mp4":
      return "mp4";
    case "video/webm":
      return "webm";
    case "video/quicktime":
      return "mov";
    case "video/x-m4v":
      return "m4v";
    default:
      return "mp4";
  }
}

/**
 * True when a MIME string denotes a video at all (any `video/*` type), not just
 * one of the accepted-for-upload set. This is the broad "is this a video?"
 * predicate used to classify existing attachments; upload gating uses the
 * narrower `VIDEO_ACCEPTED_TYPES` whitelist instead. Null/undefined/blank →
 * false.
 */
export function isVideoMimeType(type: string | null | undefined): boolean {
  return typeof type === "string" && type.toLowerCase().trim().startsWith("video/");
}

/**
 * True when an attachment-shaped object is a video, by its declared MIME type.
 * Tolerant of null/undefined and of a missing/null `type` field so callers can
 * pass raw attachment records without pre-checking.
 */
export function isVideoAttachment(
  att: { type?: string | null } | null | undefined,
): boolean {
  return !!att && isVideoMimeType(att.type);
}

/**
 * Per-plan video hosting limits. This is the single source of truth read by the
 * signed-URL and confirm endpoints — do not inline these numbers elsewhere.
 */
export interface VideoPlanLimits {
  /** Largest single video (bytes) this plan may upload. */
  maxVideoSizeBytes: number;
  /** How many videos this plan may host per project. */
  maxVideosPerProject: number;
  /** Whether this plan can ingest a live stream (Mux live). */
  liveStreamIngest: boolean;
}

// Per-video size is a single universal 1 GB cap on every tier — matching the
// hard client ceiling above — so the composer can reject an oversize file at
// pick time without knowing the user's plan. Tiers differentiate on video
// COUNT and live ingest instead.
const FREE_VIDEO_LIMITS: VideoPlanLimits = {
  maxVideoSizeBytes: VIDEO_MAX_UPLOAD_BYTES_HARD,
  maxVideosPerProject: 3,
  liveStreamIngest: false,
};

const BUILD_VIDEO_LIMITS: VideoPlanLimits = {
  maxVideoSizeBytes: VIDEO_MAX_UPLOAD_BYTES_HARD,
  maxVideosPerProject: 20,
  liveStreamIngest: true,
};

const GROW_VIDEO_LIMITS: VideoPlanLimits = {
  maxVideoSizeBytes: VIDEO_MAX_UPLOAD_BYTES_HARD,
  maxVideosPerProject: 50,
  liveStreamIngest: true,
};

/**
 * Resolve the video limits for a plan id. TOTAL over its input: the parameter is
 * typed as a plain string (routes pass a raw plan column) and any
 * unknown/null/undefined/blank value falls back to the FREE limits, so a loading
 * or malformed plan can never unlock a larger quota. The legacy "starter" alias
 * shares the paid "pro" build-tier limits.
 */
export function videoLimitsForPlan(
  planId: string | null | undefined,
): VideoPlanLimits {
  switch (planId) {
    case "starter":
    case "pro":
      return BUILD_VIDEO_LIMITS;
    case "grow":
    case "scale":
      return GROW_VIDEO_LIMITS;
    default:
      return FREE_VIDEO_LIMITS;
  }
}
